import {
  colorToBlockId,
  type BlockId,
} from '../../minigame/match2-timestep/game/defs/block';
import type { BasicBlockEntity } from '../../minigame/match2-timestep/game/entities/BlockEntity';
import type { GameScene } from '../../minigame/match2-timestep/game/GameScene';
import { arrayShuffle } from '../../minigame/match2-timestep/lib/util/jsTools';
import type { RandomFunction } from '../../minigame/match2-timestep/lib/util/mathTools';
import { waitFor } from '../../minigame/match2-timestep/lib/util/waitFor';
import {
  runSkySummonerPowerAnimation,
  type SkySummonerPowerFlight,
  type SkySummonerPowerSpawnShake,
} from './dreamDrift.power.summoner.anim';

/** Tuning values for the Sky Summoner opening power. */
type SkySummonerPowerConfig = {
  /** Number of Firecracker power blocks placed on the starting board. */
  firecrackerCount: number;
  /** Number of Water Balloon power blocks placed on the starting board. */
  waterBalloonCount: number;
  /** Number of color-matched Lollipops placed after the other powers. */
  lollipopCount: number;
};

/** Current Sky Summoner tuning. */
const SKY_SUMMONER_POWER_CONFIG: SkySummonerPowerConfig = {
  firecrackerCount: 1,
  waterBalloonCount: 1,
  lollipopCount: 1,
};

/** Delay between consecutive Sky Summoner power-block spawns. */
const SKY_SUMMONER_SPAWN_INTERVAL_MS = 200;

/**
 * Build the balanced power-block queue requested by Sky Summoner tuning.
 *
 * @param config Power-local counts for each supported power-block kind.
 * @param random Random source used to select Firecracker orientations.
 * @returns Alternating Firecracker and Water Balloon ids followed by uncolored Lollipops.
 */
export function createSkySummonerPowerBlockIds(
  config: SkySummonerPowerConfig,
  random: RandomFunction = Math.random,
): (BlockId | 'cube')[] {
  // Ignore invalid fractional or negative tuning without blocking level startup.
  const firecrackerCount = Math.max(0, Math.floor(config.firecrackerCount));
  const waterBalloonCount = Math.max(0, Math.floor(config.waterBalloonCount));
  const lollipopCount = Math.max(0, Math.floor(config.lollipopCount));
  const powerBlockIds: (BlockId | 'cube')[] = [];

  // Alternate the two kinds so limited boards retain a balanced useful mix.
  const maximumCount = Math.max(firecrackerCount, waterBalloonCount);
  for (let index = 0; index < maximumCount; index++) {
    if (index < firecrackerCount) {
      const firecrackerId =
        random() < 0.5 ? 'rocketHorizontal' : 'rocketVertical';
      powerBlockIds.push(firecrackerId);
    }

    if (index < waterBalloonCount) {
      powerBlockIds.push('bomb');
    }
  }

  // Append Lollipops; their colors are resolved after placement targets are selected.
  for (let index = 0; index < lollipopCount; index++) {
    powerBlockIds.push('cube');
  }

  // Return the complete power-block queue for placement.
  return powerBlockIds;
}

/**
 * Collect randomized active basic blocks that are safe for Sky Summoner.
 *
 * @param game Active Dream Drift Match-2 runtime.
 * @param count Maximum number of placement targets required.
 * @param random Random source used to vary placement cells.
 * @returns Up to the requested number of valid objective-safe basic blocks.
 */
export function collectSkySummonerPlacementTargets(
  game: GameScene,
  count: number,
  random: RandomFunction = Math.random,
): BasicBlockEntity[] {
  // Non-positive placement requests do not need to inspect the board.
  const placementCount = Math.max(0, Math.floor(count));
  if (placementCount === 0) return [];

  // Snapshot only live basic blocks whose board cells remain valid and safe.
  const activeBlocks = game.basicBlockSystem.getActiveBasicBlocks();
  const safeBlocks = activeBlocks.filter((block) =>
    isSkySummonerPlacementTarget(game, block),
  );

  // Preserve uncovered-only targeting whenever any live basic is uncovered.
  const map = game.sessionEntity.c.map;
  const hasUncoveredBasic = activeBlocks.some((block) => {
    const cell = map.getCellAt(block.c.position.mapPosition);
    return cell?.enabled && cell.base?.entity === block && !cell.overlay;
  });
  const candidates = hasUncoveredBasic
    ? safeBlocks.filter(
        (block) => !map.getCellAt(block.c.position.mapPosition)?.overlay,
      )
    : safeBlocks;

  // Randomize equally safe cells and cap placement to the available board space.
  arrayShuffle(candidates, random);
  return candidates.slice(0, placementCount);
}

/**
 * Place Sky Summoner's configured Firecrackers, Water Balloons, and Lollipops.
 *
 * @param game Active Dream Drift Match-2 runtime.
 */
export async function runSkySummonerPower(game: GameScene): Promise<void> {
  // Build the complete configured mix before selecting board targets.
  const powerBlocks = createSkySummonerPowerBlockIds(SKY_SUMMONER_POWER_CONFIG);
  const targets = collectSkySummonerPlacementTargets(game, powerBlocks.length);

  // Match each Lollipop to its original basic tile before any placement changes the board.
  const powerBlockIds = targets.map((target, index): BlockId => {
    const powerBlock = powerBlocks[index];
    if (powerBlock !== 'cube') return powerBlock;

    // Narrow the engine's generic block props before reading the basic tile color.
    const props = target.c.block.props;
    if (props.type !== 'basic') {
      throw new Error('Sky Summoner Lollipop target must be a basic tile');
    }

    // Use the same color-to-Lollipop mapping as normal Match-2 combinations.
    return colorToBlockId[props.color].cube!;
  });

  let placementStarted = false;

  /** Check the live Match-2 runtime state without retaining a narrowed snapshot. */
  const gameHasClosed = (): boolean => game.active === false;

  /** Place the selected powers at most once during the authored idle phase. */
  const placePowerBlocks = async (
    animatePowerBlock: SkySummonerPowerFlight,
    shakePowerSpawn: SkySummonerPowerSpawnShake,
  ): Promise<void> => {
    // Ignore repeated presentation callbacks for this level opening.
    if (placementStarted) return;
    placementStarted = true;

    // Empty or interrupted boards still complete the visual presentation safely.
    if (targets.length === 0 || gameHasClosed()) return;

    /** Animate and materialize one selected power block on the board. */
    const placePowerBlock = async (index: number): Promise<void> => {
      // Stop before creating another temporary view when the game has closed.
      if (gameHasClosed()) return;

      // Fly the temporary power block from the wormhole to its selected cell.
      await animatePowerBlock(targets[index], powerBlockIds[index]);

      // Skip the real board spawn if the scene closed during the flight.
      if (gameHasClosed()) return;

      // Replace the basic tile using the standard power-block spawn behavior.
      const spawn = game.basicBlockSystem.turnBlockInto(
        targets[index],
        powerBlockIds[index],
      );

      // Shake after placement starts and wait for both animations to finish.
      const shake = shakePowerSpawn();
      await Promise.all([spawn, shake]);
    };

    // Launch each flight at the existing stagger while earlier flights continue.
    const placementPromises: Promise<void>[] = [];
    for (let index = 0; index < targets.length; index++) {
      placementPromises.push(placePowerBlock(index));

      // Leave a short beat before launching the next temporary power block.
      const isLastTarget = index === targets.length - 1;
      if (isLastTarget) continue;
      await waitFor(SKY_SUMMONER_SPAWN_INTERVAL_MS);
    }

    // Wait until every flight has landed and spawned its real board block.
    await Promise.all(placementPromises);

    // Skip dependent visual updates if the scene closed during placement.
    if (gameHasClosed()) return;

    // Refresh dependent visuals after every summoned power block is present.
    game.powerBlockSystem.refreshComboGlows();
    game.basicBlockSystem.updateBlockSymbols();
  };

  // Play the complete hero presentation around the existing placement behavior.
  await runSkySummonerPowerAnimation(game, placePowerBlocks);
}

/**
 * Check whether one active basic block remains a valid objective-safe target.
 *
 * @param game Active Dream Drift Match-2 runtime.
 * @param block Candidate basic block.
 * @returns Whether Sky Summoner may replace the candidate.
 */
function isSkySummonerPlacementTarget(
  game: GameScene,
  block: BasicBlockEntity,
): boolean {
  // Reject stale query entities and cells that are no longer playable basics.
  const position = block.c.position.mapPosition;
  const map = game.sessionEntity.c.map;
  const cell = map.getCellAt(position);
  if (!cell?.enabled || cell.base?.entity !== block) return false;

  // Regular bubbles allow replacement; other overlays retain their protection.
  if (cell.overlay && cell.overlay.entity.c.block.blockId !== 'cage')
    return false;

  // Replacing a basic tile records its destruction, so never consume a color goal.
  const blockId = block.c.block.blockId;
  if (game.getGoalCount(blockId) > 0) return false;

  // The candidate is live and its replacement cannot advance a level goal.
  return true;
}
